import jsPDF from "jspdf";
import { Grade, Subject } from "../types";
import { loadRaleway } from "./ralewayFont";

/* ======================================================
   Worksheet PDF Generator (A-Typical geometry + rubric-safe tokenization)
   - unit: mm, format: a4
   - header/footer/title-page preserved
   - robust tokenization: sections/questions/rubrics regardless of line breaks
   ====================================================== */

export interface WorksheetPdfInputs {
  grade: Grade;
  subject: Subject;
  chapterTitle: string;
}

/* =========================
   Formatting constants
   ========================= */

const BRAND_ORANGE = "#F75C03";
const BRAND_GREY = "#4D4D4D";
const TEXT_COLOR = BRAND_GREY;
const LIGHT_GREY = "#F5F5F5";
const FOOTER_GREY = "#8C8C8C";

const ptToMm = (pt: number) => pt * (25.4 / 72);

const H1_SIZE = 18;
const H2_SIZE = 14;
const BODY_SIZE = 11;
const SMALL_SIZE = 9.5;
const FOOTER_SIZE = 8;
const LINE_HEIGHT_FACTOR = 1.35;
const PARA_SPACING = ptToMm(6);
const SECTION_SPACING = ptToMm(10);

// Rubric print spacing (fixed)
const RUBRIC_Q_BLOCK_GAP_MM = 4;      // extra space between Q blocks
const RUBRIC_LABEL_BLOCK_GAP_MM = 2;  // extra space before each rubric label block

const MARGIN = 25;

const HEADER_TOP = 10;
const HEADER_H = 28;
const BODY_START_Y = HEADER_TOP + HEADER_H + 8;

/* =========================
   Module-level state
   ========================= */

let doc: jsPDF;
let pageHeight = 0;
let pageWidth = 0;
let yCursor = 0;

let FONT_FAMILY: "Raleway" | "helvetica" = "helvetica";

let cachedBrandLogoDataUrl: string | null = null;
let cachedTenantLogoDataUrl: string | null = null;

/* =========================
   Small helpers
   ========================= */

function hexToRgb(hex: string) {
  const c = hex.replace("#", "");
  const bigint = parseInt(
    c.length === 3 ? c.split("").map((x) => x + x).join("") : c,
    16
  );
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}

function setFill(pdf: jsPDF, hex: string) {
  const { r, g, b } = hexToRgb(hex);
  pdf.setFillColor(r, g, b);
}

function setDraw(pdf: jsPDF, hex: string) {
  const { r, g, b } = hexToRgb(hex);
  pdf.setDrawColor(r, g, b);
}

function setText(pdf: jsPDF, hex: string) {
  const { r, g, b } = hexToRgb(hex);
  pdf.setTextColor(r, g, b);
}

function setFontConsistent(style: "normal" | "bold") {
  try {
    doc.setFont(FONT_FAMILY, style);
  } catch {
    FONT_FAMILY = "helvetica";
    doc.setFont("helvetica", style);
  }
}

async function loadImageAsDataUrl(urlOrDataUrl: string): Promise<string> {
  const s = String(urlOrDataUrl || "").trim();
  if (!s) throw new Error("empty_image");
  if (s.startsWith("data:image/")) return s;

  const res = await fetch(s);
  if (!res.ok) throw new Error(`image_fetch_failed:${res.status}`);
  const blob = await res.blob();

  const dataUrl: string = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("image_read_failed"));
    reader.onload = () => resolve(String(reader.result || ""));
    reader.readAsDataURL(blob);
  });

  if (!dataUrl.startsWith("data:image/")) throw new Error("image_dataurl_invalid");
  return dataUrl;
}

function safeAddImage(pdf: jsPDF, dataUrl: string, x: number, y: number, w: number, h: number) {
  const s = String(dataUrl || "").trim();
  if (!s.startsWith("data:image/")) return;

  let fmt: "PNG" | "JPEG" = "PNG";
  if (s.startsWith("data:image/jpeg") || s.startsWith("data:image/jpg")) fmt = "JPEG";

  try {
    pdf.addImage(s, fmt, x, y, w, h);
  } catch {
    // ignore
  }
}

/* =========================
   Header / Footer (A-Typical)
   ========================= */

async function drawHeader(pageNo: number, reportTitle: string, brandLogo: string, tenantLogoUrl?: string) {
  const mx = MARGIN;
  const top = HEADER_TOP;

  const brandW = 26;
  const brandH = 14;

  const tenantW = 26;
  const tenantH = 14;

  // Logos (best-effort)
  try {
    if (!cachedBrandLogoDataUrl) cachedBrandLogoDataUrl = await loadImageAsDataUrl(brandLogo);
    safeAddImage(doc, cachedBrandLogoDataUrl, mx, top, brandW, brandH);
  } catch {}

  try {
    const t = String(tenantLogoUrl || "").trim();
    if (t) {
      if (!cachedTenantLogoDataUrl) cachedTenantLogoDataUrl = await loadImageAsDataUrl(t);
      safeAddImage(doc, cachedTenantLogoDataUrl, pageWidth - mx - tenantW, top, tenantW, tenantH);
    }
  } catch {}

  // Report title
  setText(doc, BRAND_ORANGE);
  doc.setFontSize(12);
  setFontConsistent("bold");
  doc.text(reportTitle, mx, top + brandH + 10);

  // Divider line
  setDraw(doc, "#E6E6E6");
  doc.line(mx, top + HEADER_H, pageWidth - mx, top + HEADER_H);

  // Reset body defaults
  setText(doc, TEXT_COLOR);
  doc.setFontSize(BODY_SIZE);
  setFontConsistent("normal");
}

async function addNewPageWithHeader(
  pageNoRef: { n: number },
  reportTitle: string,
  brandLogo: string,
  tenantLogoUrl?: string
) {
  doc.addPage();
  pageNoRef.n += 1;
  await drawHeader(pageNoRef.n, reportTitle, brandLogo, tenantLogoUrl);
  yCursor = BODY_START_Y;
}

async function checkPageBreak(
  neededMm: number,
  pageNoRef: { n: number },
  reportTitle: string,
  brandLogo: string,
  tenantLogoUrl?: string
) {
  const bottomY = pageHeight - 18;
  if (yCursor + neededMm > bottomY) {
    await addNewPageWithHeader(pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
  }
}

function addFooter() {
  const totalPages = (doc as any).internal.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    doc.setFontSize(FOOTER_SIZE);
    setFontConsistent("normal");
    setText(doc, FOOTER_GREY);

    doc.text("© 2026 One Size Fits None™", MARGIN, pageHeight - 10);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - MARGIN, pageHeight - 10, { align: "right" });

    // Optional tiny tenant logo (cache-only)
    try {
      if (cachedTenantLogoDataUrl) {
        safeAddImage(doc, cachedTenantLogoDataUrl, MARGIN, pageHeight - 18, 10, 10);
      }
    } catch {}
  }
}

/* =========================
   Title page (A-Typical)
   ========================= */

async function addTitlePage(
  inputs: WorksheetPdfInputs,
  brandLogo: string,
  tenantLogoUrl: string | undefined,
  reportTitle: string,
  pageNoRef: { n: number }
) {
  pageNoRef.n = 1;
  await drawHeader(1, reportTitle, brandLogo, tenantLogoUrl);

  yCursor = pageHeight / 3.5;

  setText(doc, BRAND_GREY);
  doc.setFontSize(24);
  setFontConsistent("bold");
  doc.text("Practice Worksheet", pageWidth / 2, yCursor, { align: "center" });

  setFontConsistent("normal");
  yCursor += 25;

  const boxWidth = 140;
  const boxX = (pageWidth - boxWidth) / 2;

  setDraw(doc, "#DDDDDD");
  setFill(doc, "#FFFFFF");
  doc.roundedRect(boxX, yCursor, boxWidth, 46, 3, 3, "FD");

  const lx = boxX + 10;
  const vx = boxX + 55;

  doc.setFontSize(BODY_SIZE);
  setFontConsistent("bold");
  setText(doc, TEXT_COLOR);
  doc.text("Chapter:", lx, yCursor + 12);
  doc.text("Grade:", lx, yCursor + 24);
  doc.text("Subject:", lx, yCursor + 36);

  setFontConsistent("normal");

  const grade = String(inputs.grade || "").replace(/^Grade\s*/i, "").trim();
  const subject = String(inputs.subject || "").trim();
  const chapter = String(inputs.chapterTitle || "").trim();

  doc.text(chapter, vx, yCursor + 12);
  doc.text(grade, vx, yCursor + 24);
  doc.text(subject, vx, yCursor + 36);

  await addNewPageWithHeader(pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
}

/* =========================
   Tokenization (permanent)
   ========================= */

function stripNoise(s: string) {
  let t = String(s || "").replace(/\r/g, "");

  // Remove boilerplate echoes
  t = t.replace(/^\s*Worksheet Header\s*$/gim, "");
  t = t.replace(/^\s*Worksheet Body\s*$/gim, "");
  t = t.replace(/^\s*Worksheet\s*$/gim, "");
  t = t.replace(/^\s*Practice Worksheet\s*$/gim, "");

  // Remove separators / junk lines
  t = t.replace(/^\s*---\s*$/gim, "");
  t = t.replace(/^\s*#{1,6}\s*$/gim, "");
  t = t.replace(/^\s*\*\s*$/gim, "");

  // Normalize spaces
  t = t.replace(/\t/g, " ");
  t = t.replace(/[ ]{2,}/g, " ");

  return t.trim();
}

function tokenize(text: string) {
  let t = stripNoise(text);

  // Normalize bullets: force each bullet to start its own line
  t = t.replace(/•\s*/g, "\n- ");
  t = t.replace(/\n-\s*-\s*/g, "\n- ");

  // PATCH C: Convert rubric label bullets into plain headers BEFORE boundary forcing.
  // This prevents "- expected_points:" from being split into "-" + "expected_points:".
  t = t.replace(/(^|\n)-\s*expected_points\s*:\s*/gi, "$1Expected Points:\n");
  t = t.replace(/(^|\n)-\s*acceptable_variations\s*:\s*/gi, "$1Acceptable Variations:\n");
  t = t.replace(/(^|\n)-\s*common_errors\s*:\s*/gi, "$1Common Errors:\n");

  // Force Teacher Rubrics boundary
  t = t.replace(/\s*(Teacher Rubrics)\s*/gi, "\n$1\n");
  t = t.replace(/Teacher Rubrics\s*(Q\d+|Question\s+\d+)/gi, "Teacher Rubrics\n$1");

  // Force Section headers to line boundaries
  t = t.replace(/\s*(Section\s+[ABC]\s*:\s+)/gi, "\n$1");
  t = t.replace(/(Section\s+[ABC]\s*:\s+[^\n]+)\s*/gi, "\n$1\n");

  // Force Q headers to line boundaries
  t = t.replace(/\s*(Q\d+)\s*/gi, "\n$1\n");
  t = t.replace(/\s*(Question\s+\d+)\s*/gi, "\n$1\n");

  // Force rubric labels to line boundaries (underscored or title-case)
  // NOTE: after PATCH C, underscored forms should be mostly gone, but keep these for safety.
  t = t.replace(/\s*(expected_points\s*:)\s*/gi, "\n$1\n");
  t = t.replace(/\s*(acceptable_variations\s*:)\s*/gi, "\n$1\n");
  t = t.replace(/\s*(common_errors\s*:)\s*/gi, "\n$1\n");

  t = t.replace(/\s*(Expected Points)\s*/gi, "\nExpected Points:\n");
  t = t.replace(/\s*(Acceptable Variations)\s*/gi, "\nAcceptable Variations:\n");
  t = t.replace(/\s*(Common Errors)\s*/gi, "\nCommon Errors:\n");

  // Split mid-line numbered questions: "... situation. 12. After ..."
  t = t.replace(/([A-Za-z\)\.])\s+(\d+\.\s+)/g, "$1\n$2");

  // Split mid-line subparts: "1793. (a) What..." -> ensure (a) is on new line
  t = t.replace(/([A-Za-z0-9\)\.])\s+(\([a-dA-D]\)\s+)/g, "$1\n$2");

  // Collapse excessive newlines
  t = t.replace(/\n{3,}/g, "\n\n");

  return t
    .split("\n")
    .map((l) => l.trim())
    // PATCH C: drop orphan hyphen lines at the tokenization stage as well
    .filter((l) => !!l && l !== "-");
}


/* =========================
   Line classifiers
   ========================= */

function isSectionHeader(line: string) {
  return /^Section\s+[ABC]\s*:\s+/i.test(line.trim());
}

function isRubricHeader(line: string) {
  return /^Teacher Rubrics$/i.test(line.trim());
}

function isRubricQuestionHeader(line: string) {
  return /^(Q\d+|Question\s+\d+)$/i.test(line.trim());
}

function rubricQuestionLabel(line: string) {
  const s = line.trim();
  if (/^Q\d+$/i.test(s)) return s.toUpperCase();
  const m = s.match(/^Question\s+(\d+)$/i);
  if (m) return `Q${m[1]}`;
  return s;
}

function isRubricLabel(line: string) {
  const s = line.trim();
  return /^(expected_points|acceptable_variations|common_errors)\s*:$/i.test(s) ||
         /^(Expected Points|Acceptable Variations|Common Errors)\s*:$/i.test(s);
}

function rubricLabel(line: string) {
  const s = line.trim().replace(/:/g, "");
  if (/^expected_points$/i.test(s)) return "Expected Points";
  if (/^acceptable_variations$/i.test(s)) return "Acceptable Variations";
  if (/^common_errors$/i.test(s)) return "Common Errors";
  return s;
}

function isBullet(line: string) {
  return /^(\*|-)\s+/.test(line.trim());
}

function isNumberedQuestion(line: string) {
  return /^\d+\.\s+/.test(line.trim());
}

function isSubpart(line: string) {
  return /^\([a-dA-D]\)\s+/.test(line.trim());
}

/* =========================
   Rendering
   ========================= */

function renderSectionHeader(
  text: string,
  pageNoRef: { n: number },
  reportTitle: string,
  brandLogo: string,
  tenantLogoUrl?: string
) {
  const title = text.replace(/^#{1,6}\s+/, "").trim();

  doc.setFontSize(H2_SIZE);
  setFontConsistent("bold");
  setText(doc, BRAND_ORANGE);

  const maxW = pageWidth - 2 * MARGIN;
  const lines = doc.splitTextToSize(title, maxW);

  const lh = ptToMm(H2_SIZE) * LINE_HEIGHT_FACTOR;
  const needed = lines.length * lh + ptToMm(6);

  return (async () => {
    await checkPageBreak(needed, pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
    doc.text(lines as any, MARGIN, yCursor);
    yCursor += lines.length * lh + ptToMm(6);

    setText(doc, TEXT_COLOR);
    doc.setFontSize(BODY_SIZE);
    setFontConsistent("normal");
  })();
}

function renderParagraph(
  text: string,
  fontSize: number,
  pageNoRef: { n: number },
  reportTitle: string,
  brandLogo: string,
  tenantLogoUrl?: string
) {
  const size = fontSize || BODY_SIZE;

  doc.setFontSize(size);
  setFontConsistent("normal");
  setText(doc, TEXT_COLOR);

  const maxW = pageWidth - 2 * MARGIN;
  const lines = doc.splitTextToSize(text, maxW);
  const lh = ptToMm(size) * LINE_HEIGHT_FACTOR;
  const needed = lines.length * lh + PARA_SPACING;

  return (async () => {
    await checkPageBreak(needed, pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
    doc.text(lines as any, MARGIN, yCursor);
    yCursor += lines.length * lh + PARA_SPACING;
  })();
}

function renderBullet(
  line: string,
  pageNoRef: { n: number },
  reportTitle: string,
  brandLogo: string,
  tenantLogoUrl?: string
) {
  const t = line.trim().replace(/^(\*|-)\s+/, "");
  const bullet = "•";
  const maxW = pageWidth - 2 * MARGIN - 5;

  doc.setFontSize(BODY_SIZE);
  setFontConsistent("normal");
  setText(doc, TEXT_COLOR);

  const wrapped = doc.splitTextToSize(t, maxW);
  const lh = ptToMm(BODY_SIZE) * LINE_HEIGHT_FACTOR;
  const needed = wrapped.length * lh + PARA_SPACING;

  return (async () => {
    await checkPageBreak(needed, pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
    doc.text(bullet, MARGIN, yCursor);
    doc.text(wrapped as any, MARGIN + 5, yCursor);
    yCursor += wrapped.length * lh + PARA_SPACING;
  })();
}

function renderRubricLabelLine(
  label: string,
  pageNoRef: { n: number },
  reportTitle: string,
  brandLogo: string,
  tenantLogoUrl?: string
) {
  const bulletLine = `- ${label}`;
  return renderBullet(bulletLine, pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
}

/* =========================
   Main content render
   ========================= */

async function renderContent(
  worksheetText: string,
  brandLogo: string,
  tenantLogoUrl: string | undefined,
  reportTitle: string,
  pageNoRef: { n: number }
) {
  const lines = tokenize(worksheetText);

  // Intro panel
  await checkPageBreak(ptToMm(44), pageNoRef, reportTitle, brandLogo, tenantLogoUrl);

  const panelX = MARGIN;
  const panelW = pageWidth - 2 * MARGIN;
  const panelH = 22;

  setFill(doc, LIGHT_GREY);
  doc.roundedRect(panelX, yCursor, panelW, panelH, 2, 2, "F");

  setDraw(doc, BRAND_ORANGE);
  doc.setLineWidth(0.6);
  doc.line(panelX, yCursor, panelX, yCursor + panelH);

  const gx = panelX + 6;
  const gy = yCursor + 8;

  setText(doc, TEXT_COLOR);
  doc.setFontSize(SMALL_SIZE);
  setFontConsistent("normal");

  doc.text("Name: ____________________", gx, gy);
  doc.text("Date: ____________________", gx + 80, gy);

  yCursor += panelH + SECTION_SPACING;

  // Body
  let inRubrics = false;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      yCursor += ptToMm(3);
      continue;
    }

        if (isRubricHeader(line)) {
      // Force Teacher Rubrics to start on a fresh page
      await addNewPageWithHeader(pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
      await renderSectionHeader("Teacher Rubrics", pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
      inRubrics = true;
      continue;
    }



    if (isSectionHeader(line)) {
      await renderSectionHeader(line, pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
      continue;
    }

       if (isRubricQuestionHeader(line)) {
      if (inRubrics) {
        await checkPageBreak(RUBRIC_Q_BLOCK_GAP_MM, pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
        yCursor += RUBRIC_Q_BLOCK_GAP_MM;
      }
      await renderSectionHeader(rubricQuestionLabel(line), pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
      continue;
    }


       if (isRubricLabel(line)) {
      if (inRubrics) {
        await checkPageBreak(RUBRIC_LABEL_BLOCK_GAP_MM, pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
        yCursor += RUBRIC_LABEL_BLOCK_GAP_MM;
      }
      await renderRubricLabelLine(rubricLabel(line), pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
      continue;
    }


    if (isBullet(line)) {
      await renderBullet(line, pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
      continue;
    }

    if (isNumberedQuestion(line) || isSubpart(line)) {
      await renderParagraph(line, BODY_SIZE, pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
      continue;
    }

    await renderParagraph(line, BODY_SIZE, pageNoRef, reportTitle, brandLogo, tenantLogoUrl);
  }
}

/* =========================
   Public API
   ========================= */

export async function generateWorksheetPdf(
  worksheetText: string,
  inputs: WorksheetPdfInputs,
  brandLogo: string,
  partnerLogo: string, // retained for compatibility
  tenantLogoUrl?: string
) {
  void partnerLogo;

  cachedBrandLogoDataUrl = null;
  cachedTenantLogoDataUrl = null;

  doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  pageHeight = doc.internal.pageSize.height;
  pageWidth = doc.internal.pageSize.width;

  FONT_FAMILY = loadRaleway(doc) ? "Raleway" : "helvetica";
  doc.setFont(FONT_FAMILY, "normal");

  const reportTitle = "Practice Worksheet";
  const pageNoRef = { n: 1 };

  await addTitlePage(inputs, brandLogo, tenantLogoUrl, reportTitle, pageNoRef);
  await renderContent(worksheetText, brandLogo, tenantLogoUrl, reportTitle, pageNoRef);

  addFooter();

  const safeName = String(inputs.chapterTitle || "Worksheet")
    .trim()
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, "")
    .replace(/\s+/g, "_")
    .slice(0, 80);

  doc.save(`Worksheet-${safeName}.pdf`);
}
